import { PLAGIARISM } from '@src/components/Cards/constants'
import * as documentActions from '@src/state/document/actions'
import * as sessionActions from '@src/state/session/actions'
import {
  CheckActions,
  DocumentActions,
  GlobalActions,
  SessionActions,
} from '@src/types/actions'
import { EditorState } from 'draft-js'
import { getType } from 'typesafe-actions'
import { resetState } from '../actions'
import { BLOCK_KEY } from './document.helper'

export type Metadata = documentActions.GetPaperSubjectResponse['metadata'] & {
  kgSubjectName?: string
  kgSubjectUuid?: string | null
}

export interface DocumentState {
  readonly version: string
  readonly editorState: EditorState
  readonly loading: boolean
  readonly saving: boolean
  readonly copied: boolean
  readonly subject?: string
  readonly score?: number
  readonly metadata?: Metadata
  comments: documentActions.CommentsMap
  plagiarismScore?: number
}

export const initialState: DocumentState = {
  version: BLOCK_KEY,
  editorState: EditorState.createEmpty(),
  loading: true,
  saving: false,
  copied: false,
  comments: {},
  metadata: {},
}

type Actions = DocumentActions | SessionActions | CheckActions | GlobalActions

export default (state: DocumentState = initialState, action: Actions): DocumentState => {
  switch (action.type) {
    case getType(documentActions.clearDocument):
    case getType(documentActions.handleDocumentCreate): {
      return initialState
    }
    case getType(documentActions.deletePlagiarismScore): {
      return {
        ...state,
        plagiarismScore: undefined,
      }
    }
    case getType(documentActions.setPlagiarismScore): {
      const { score } = action.payload
      return {
        ...state,
        plagiarismScore: score,
      }
    }
    case getType(documentActions.deleteAllPlagiarismComments): {
      const grammarComments = Object.entries(state.comments).reduce<
        documentActions.CommentsMap
      >((map, v) => {
        const [id, comment] = v
        if (comment.type === PLAGIARISM) {
          return map
        }
        return (map = { ...map, [id]: comment })
      }, {})
      return { ...state, comments: grammarComments }
    }
    case getType(documentActions.deleteAllGrammarComments): {
      const plagiarismComments = Object.entries(state.comments).reduce<
        documentActions.CommentsMap
      >((map, v) => {
        const [id, comment] = v
        if (comment.type !== PLAGIARISM) {
          return map
        }
        return { ...map, [id]: comment }
      }, {})
      return {
        ...state,
        comments: plagiarismComments,
      }
    }
    case getType(documentActions.deleteComments): {
      const newComments = Object.entries(state.comments).reduce<
        documentActions.CommentsMap
      >((map, v) => {
        const [id, comment] = v
        if (action.payload.ids.includes(id)) {
          return map
        }
        return { ...map, [id]: comment }
      }, {})
      return {
        ...state,
        comments: newComments,
      }
    }
    case getType(documentActions.deleteComment): {
      const { [action.payload.id]: value, ...newComments } = state.comments
      return {
        ...state,
        comments: newComments,
      }
    }
    case getType(documentActions.updateComments): {
      const commentsToUpdate = action.payload.reduce<documentActions.CommentsMap>(
        (map, comment) => {
          if (comment) {
            map[comment.id] = comment
          }
          return map
        },
        {}
      )
      return {
        ...state,
        comments: { ...state.comments, ...commentsToUpdate },
      }
    }
    case getType(documentActions.updateComment): {
      return {
        ...state,
        comments: {
          ...state.comments,
          [action.payload.id]: action.payload,
        },
      }
    }
    case getType(documentActions.setComments): {
      return {
        ...state,
        comments: action.payload,
      }
    }
    case getType(documentActions.addComments): {
      return {
        ...state,
        comments: {
          ...state.comments,
          ...action.payload,
        },
      }
    }
    case getType(documentActions.getPaperSubject.success): {
      const { score, subject, metadata, kgName, kgUuid } = action.payload
      return {
        ...state,
        subject,
        score,
        metadata: {
          ...metadata,
          kgSubjectName: kgName,
          kgSubjectUuid: kgUuid,
        },
      }
    }
    case getType(documentActions.setEditorState): {
      const contentState = action.payload.editorState.getCurrentContent()
      const newES = EditorState.push(
        action.payload.editorState,
        contentState,
        'apply-entity'
      )
      return {
        ...state,
        editorState: newES,
      }
    }
    case getType(documentActions.getDocument.request): {
      return {
        ...state,
        loading: true,
      }
    }
    case getType(sessionActions.loadFromLocalStorage):
    case getType(documentActions.getDocument.failure):
    case getType(documentActions.getDocument.success): {
      return {
        ...state,
        loading: false,
      }
    }
    case getType(documentActions.saveDocument.request): {
      return {
        ...state,
        saving: true,
      }
    }
    case getType(documentActions.saveDocument.success):
    case getType(documentActions.saveDocument.failure): {
      return {
        ...state,
        saving: false,
        loading: false,
      }
    }
    case getType(resetState): {
      return initialState
    }
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/state/document/reducer.ts